/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { TimeUnitsInMs } from '../../../../core';
import { getEmptyToken, isAccessTokenValid } from '../../utils';
import { TokenStorage } from '../../../../helpers/storages';
import { CompanyPrefixes } from '../../../../enums';

import type { BearerToken } from '../../interfaces';
import type { TokenManagerConfigParams } from './interfaces';

/* eslint-disable @typescript-eslint/naming-convention */
export class TokenManagerConfig {

    protected readonly tokenStorage: TokenStorage<BearerToken>;

    protected readonly companyPrefix: CompanyPrefixes;
    protected readonly authUrl: string;
    protected readonly logoutUrl: string | null;
    protected readonly refreshTokenLiveTimeInMs: number;
    protected readonly reduceTokenLiveCoef: number;

    protected lastActiveTime: number;
    protected tokenTimerId: number | null = null;
    protected onTokenUpdateHandler: (() => void) | null = null;

    constructor(tokenManagerConfigParams: TokenManagerConfigParams) {
        const {
            companyPrefix = CompanyPrefixes.VeeamRcop,
            authUrl,
            logoutUrl = '/Login/Logout',
            refreshTokenLiveTimeInMs = TimeUnitsInMs.Hour,
            reduceTokenLiveCoef = 6,
        } = tokenManagerConfigParams;

        this.companyPrefix = companyPrefix;
        this.authUrl = authUrl;
        this.logoutUrl = logoutUrl;
        this.refreshTokenLiveTimeInMs = refreshTokenLiveTimeInMs;
        this.reduceTokenLiveCoef = reduceTokenLiveCoef;
        this.lastActiveTime = Date.now();
        this.tokenStorage = new TokenStorage<BearerToken>(companyPrefix);
        this.tokenStorage.onLastActiveTimeUpdate(() => this.setAutoLogout());
        this.tokenStorage.onTokenUpdate(() => this.onTokenUpdateHandler?.());
    }

    startTimer(sessionExpirationTimeInSeconds?: number): void {
        const sessionExpirationTime = sessionExpirationTimeInSeconds
            ? sessionExpirationTimeInSeconds * TimeUnitsInMs.Second
            : this.getRefreshTokenLiveTimeInMs();

        this.setLastActionTime();
        this.setSessionExpirationTime(sessionExpirationTime);
    }

    getRefreshTokenLiveTimeInMs(): number {
        return this.refreshTokenLiveTimeInMs;
    }

    getAuthUrl(): string {
        return this.authUrl;
    }

    getLogoutUrl(): string | null {
        return this.logoutUrl;
    }

    getReduceTokenLiveCoef(): number {
        return this.reduceTokenLiveCoef;
    }

    getBearerToken(): BearerToken | null {
        return this.tokenStorage.getToken();
    }

    setBearerToken(token: BearerToken): void {
        this.tokenStorage.setToken(token);
    }

    removeBearerToken(logout = false): void {
        this.setBearerToken({ ...getEmptyToken(), logout });
    }

    getSessionExpirationTime(): number {
        return this.tokenStorage.getSessionExpirationTime() ?? this.getRefreshTokenLiveTimeInMs();
    }

    setSessionExpirationTime(sessionExpirationTime: number): void {
        this.tokenStorage.setSessionExpirationTime(sessionExpirationTime);
    }

    getLastActionTime(): number {
        return this.tokenStorage.getLastActiveTime() ?? this.lastActiveTime;
    }

    setLastActionTime(): void {
        const now = Date.now();

        this.lastActiveTime = now;
        this.tokenStorage.setLastActiveTime(now);
    }

    getTokenTimerId(): number | null {
        return this.tokenTimerId;
    }

    setOnTokenUpdateHandler(func: () => void): void {
        this.onTokenUpdateHandler = func;
    }

    isRefreshTokenValid(expiresIn: number | void): boolean {
        const lastActiveTime = this.getLastActionTime();
        const sessionExpirationTime = this.getSessionExpirationTime();

        if (lastActiveTime) {
            return (lastActiveTime + sessionExpirationTime) > Date.now();
        }

        return isAccessTokenValid(expiresIn);
    }

    setAutoLogout(): void {
        this.resetAutoLogout();

        const sessionExpirationTime = this.getSessionExpirationTime();

        if (sessionExpirationTime) {
            this.tokenTimerId = window.setTimeout(() => this.removeBearerToken(true), sessionExpirationTime);
        }
    }

    resetAutoLogout(): void {
        if (this.tokenTimerId) {
            clearTimeout(this.tokenTimerId);
            this.tokenTimerId = null;
        }
    }

}
